import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/** Total time a single query may spend fetching, retries included. */
const SEARCH_DEADLINE_MS = 8000;
/** Pause between retries within that budget. */
const SEARCH_RETRY_DELAY_MS = 700;
/** Longer pause after a rate limit — retrying fast makes a 429 worse. */
const SEARCH_RATE_LIMIT_DELAY_MS = 1500;
/**
 * Shortest query worth sending. One letter is never a useful suggestion, and
 * the endpoint is rate limited: typing "ty" used to send "t" on the way, which
 * spent an allowance the real query needed and arrived first — so when the
 * real one was throttled, the shopper was left reading the "t" hits. That is
 * the "I typed ty and it showed me t products" report. Wait for two.
 */
const SEARCH_MIN_QUERY_LENGTH = 2;

/**
 * Opt-in diagnostics. snippets/header-search-bar.liquid publishes its
 * ?searchdebug=1 overlay as window.__ssSearchDebug; without the flag nothing
 * is published and this is a no-op. It exists because the overlay used to
 * cover only the pre-hydration handler, so it fell silent the moment the
 * component took over — which is the path every real shopper is on.
 * @param {string} message
 */
const debugLog = (message) => {
  try {
    /** @type {any} */ (window).__ssSearchDebug?.('[component] ' + message);
  } catch (e) {
    /* diagnostics must never break search */
  }
};
/** Last resort: how long a "Searching..." panel may stay up unreplaced. */
const SEARCH_PLACEHOLDER_MAX_MS = 10000;
/**
 * Suggest responses already fetched this page view, keyed by the exact query.
 *
 * The endpoint is rate limited, so the cheapest request is the one never sent.
 * Shoppers re-search the same words constantly — clear the field and type it
 * again, backspace and retype, come back to a query after trying another — and
 * every one of those was a fresh request against a finite allowance. Suggestions
 * for a given word do not change within a page view, so serve them from here.
 */
const searchResultsCache = new Map();
/** Keep the cache from growing without bound on a long browsing session. */
const SEARCH_CACHE_MAX = 30;

/** Marks our own placeholder so the watchdog never clears real results. */
const SEARCHING_PANEL =
  '<div class="header-search-suggest"><div class="header-search-suggest__inner" data-search-results data-ss-searching>' +
  '<p class="header-search-suggest__none">Searching\u2026</p></div></div>';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #placeholderWatchdog;

  #awaitingRefs = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  /**
   * The HTML parser inserts a custom element into the document as soon as it
   * reads the OPEN tag, so connectedCallback can run before a single child
   * exists. Whether that happens is a race: it needs the element's class to be
   * defined already, which a deferred module never is, but an async one can
   * be. And when it does happen the component does not merely start early — it
   * dies. Component#connectedCallback calls #updateRefs(), which THROWS a
   * MissingRefError before reaching the requestIdleCallback that starts the
   * MutationObserver watching for refs arriving. So nothing ever retries:
   * `refs` stays empty, no bindings are wired, and the bar is inert for the
   * whole page view — while `:defined` is already true, so the inline
   * first-keystroke handler stands down and does not cover for it either.
   * Typing does nothing at all, on the heavy page only, until you navigate.
   *
   * So when a required ref is not in the subtree yet, do not connect at all.
   * Watch for the children and connect once they are actually there.
   * @returns {boolean} true when connecting was deferred.
   */
  #deferUntilRefsExist() {
    const required = this.requiredRefs;
    if (!required?.length) return false;

    // Mirror Component#updateRefs, which collects refs from `roots` — the
    // element AND its shadow root. Looking only at the light DOM would make a
    // component that ever gains a declarative shadow template defer forever,
    // i.e. exactly the failure this guard exists to prevent.
    const present = () =>
      required.every((ref) => this.roots.some((root) => root.querySelector(`[ref="${ref}"]`)));
    if (present()) return false;
    if (this.#awaitingRefs) return true;

    this.#awaitingRefs = true;
    const observer = new MutationObserver(() => {
      if (!present()) return;
      observer.disconnect();
      this.#awaitingRefs = false;
      if (this.isConnected) this.connectedCallback();
    });
    observer.observe(this, { childList: true, subtree: true });
    return true;
  }

  connectedCallback() {
    if (this.#deferUntilRefsExist()) return;

    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();

      // Hydration catch-up. The visible header search bar is typeable the
      // instant it paints, but this component's module graph finishes
      // downloading later — so on a COLD first load the shopper can type a
      // whole query before the element upgrades, and every `input` event
      // fired before then is lost (nothing was listening yet). Stock only
      // restores the reset button here, never the suggestions, so the
      // dropdown stayed dead until a navigation warmed the cache. Re-run
      // the search for whatever is already in the field so it catches up.
      // (No-op for the mobile modal, whose input is empty on load.)
      const pendingTerm = this.refs.searchInput.value.trim();
      // ...unless the inline handler already painted exactly this query. Its
      // results are the same ones we would fetch, and the endpoint is rate
      // limited, so re-requesting them spends an allowance for nothing.
      const painted = this.refs.predictiveSearchResults.getAttribute('data-ss-query');
      if (pendingTerm.length >= SEARCH_MIN_QUERY_LENGTH && painted !== pendingTerm) {
        this.#getSearchResults(pendingTerm);
      }
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    // NOTHING PRIMES THE EMPTY STATE ON CONNECT, deliberately. Stock Horizon
    // did, at idle, whenever there was browsing history — and priming calls
    // resetSearch(), whose first synchronous act is `searchInput.value = ''`.
    // On the always-visible header bar that raced the shopper and won: their
    // typing vanished and the dropdown went blank, which is the bug behind
    // weeks of "search does nothing on the homepage" reports. The modal now
    // loads the panel when the dialog OPENS (#handleDialogOpen), which is the
    // only moment it can actually be seen, costs nothing until the shopper
    // taps search, and cannot collide with anything they have typed.
    // Do not reinstate a connect-time primer.
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    // Loaded even with no browsing history. With history it is the shopper's
    // recently viewed products; without, it is the New Arrivals row — which is
    // the whole point of showing a first-time shopper something useful the
    // moment they tap search, instead of an empty box.
    if (!this.#emptyStateLoaded) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // Ignore only SYNTHETIC input events (dispatched by scripts). Trusted
    // events can legitimately lack an inputType — Chrome autofill picks,
    // the native search-field clear, Windows text-suggestion insertions —
    // and must still run the search (the stock guard dropped them all,
    // which killed suggestions for users typing via those paths).
    if (!event.inputType && !event.isTrusted) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();

    // Too short to search, but the field is NOT empty — so clear the panel
    // directly rather than going through resetSearch, whose first act is to
    // blank the input. Never take the shopper's typing away from them.
    if (searchTerm.length < SEARCH_MIN_QUERY_LENGTH) {
      this.#clearResultsPanel();
      return;
    }

    this.#markStale(searchTerm);
    this.#getSearchResults(searchTerm);
    // 300ms rather than the stock 200ms: /search/suggest is rate limited, so
    // every keystroke that does NOT become a request is allowance kept for the
    // ones that matter. Typing "laundry" at a normal pace goes from roughly
    // three requests to one.
  }, 300);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Never leave a previous query's products on screen while the next request
   * is in flight. On the heavy homepage that request can take seconds or fail
   * outright, so the shopper typed "ty" and kept reading the "t" hits — it
   * looked like search was returning the wrong products. Swap them for a
   * Searching... panel until the matching results land. Header bar only; the
   * mobile modal has its own markup and empty state.
   * @param {string} term - The query now being fetched.
   */
  #markStale(term) {
    if (!this.classList.contains('header-search-bar')) return;
    const { predictiveSearchResults } = this.refs;
    if (predictiveSearchResults.getAttribute('data-ss-query') === term) return;
    if (predictiveSearchResults.innerHTML.trim()) {
      predictiveSearchResults.innerHTML = SEARCHING_PANEL;
      predictiveSearchResults.setAttribute('data-ss-query', '');
      this.#watchPlaceholder();
    }
  }

  /**
   * A "Searching..." panel must never be the end state, and the retry/clear
   * logic alone cannot promise that: a response that lands after its own
   * controller was aborted returns silently without repainting, and a request
   * that hangs rather than failing never reaches the catch at all. Both strand
   * the placeholder, which the shopper reads as a broken search ("ty worked,
   * then I typed laundry and it stays on Searching"). So whenever the
   * placeholder goes up, arm a watchdog that clears it if nothing has replaced
   * it. It only ever removes OUR OWN marked panel, so real results and a newer
   * query's placeholder are both safe.
   */
  #watchPlaceholder() {
    clearTimeout(this.#placeholderWatchdog);
    this.#placeholderWatchdog = setTimeout(() => {
      // The element may have been torn down while this was pending.
      const predictiveSearchResults = this.refs?.predictiveSearchResults;
      if (!predictiveSearchResults?.querySelector('[data-ss-searching]')) return;
      predictiveSearchResults.replaceChildren();
      predictiveSearchResults.setAttribute('data-ss-query', '');
    }, SEARCH_PLACEHOLDER_MAX_MS);
  }

  /**
   * Remove a "Searching..." placeholder this component put up once its fetch
   * has finally failed, so the dropdown closes rather than hanging. Real
   * results are never touched (the painted query is stamped on the container),
   * and an aborted request returns before reaching here, so a newer request's
   * panel is safe.
   * @param {string} term - The query whose fetch just failed.
   */
  /** Empty the dropdown without touching the input. */
  #clearResultsPanel() {
    const { predictiveSearchResults } = this.refs;
    predictiveSearchResults.replaceChildren();
    predictiveSearchResults.setAttribute('data-ss-query', '');
  }

  #clearStale(term) {
    if (!this.classList.contains('header-search-bar')) return;
    const { predictiveSearchResults } = this.refs;
    if (predictiveSearchResults.getAttribute('data-ss-query') === term) return;
    predictiveSearchResults.replaceChildren();
    predictiveSearchResults.setAttribute('data-ss-query', '');
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    // The suggest request is fired while a page may still be loading, and on
    // the heavy homepage it can be dropped outright ("Failed to fetch") when
    // the connection pipeline is saturated — the lighter collection/search
    // pages complete it fine. A single silent failure left the shopper with no
    // suggestions and no second chance, so retry within a fixed time budget.
    //
    // Each attempt carries its OWN signal, and passing one at all matters more
    // than it looks. section-renderer only shares a single pending promise per
    // URL when no signal is given (`shouldSharePendingPromise = !signal`), so
    // the signal-less version handed the SAME hung request to every retry —
    // the retries were inert against a request that never settles, which is
    // the failure mode that leaves "Searching..." up forever. A real signal
    // also means an abandoned keystroke's request is genuinely cancelled
    // instead of staying in flight competing for the homepage's connections.
    const deadline = Date.now() + SEARCH_DEADLINE_MS;

    // Fetched here rather than through sectionRenderer.getSectionHTML, which
    // does `fetch(...).then((response) => response.text())` and NEVER CHECKS
    // response.ok. /search/suggest is rate limited — it answers HTTP 429 with
    // a "Verifying your connection" challenge page — and that page was being
    // handed to morph as though it were results. It contains no
    // [data-search-results], so the dropdown just stayed hidden: "I search
    // again and it does not load". The rate limit is per shopper and survives
    // a reload, which is why refreshing did not help and only coming back
    // later did. Owning the fetch means the status is visible, so a 429 can be
    // backed off and retried instead of silently painted.
    const requestUrl = new URL(url);
    requestUrl.searchParams.set('section_id', sectionId);
    requestUrl.searchParams.sort();

    const fetchAttempt = () => {
      const attempt = new AbortController();
      const relayAbort = () => attempt.abort();
      if (abortController.signal.aborted) attempt.abort();
      else abortController.signal.addEventListener('abort', relayAbort, { once: true });
      const budget = setTimeout(relayAbort, Math.max(0, deadline - Date.now()));

      debugLog(`fetch "${searchTerm}" (${Math.round(deadline - Date.now())}ms of budget left)`);
      return fetch(requestUrl, { signal: attempt.signal, headers: { Accept: 'text/html' } })
        .then((response) => {
          debugLog(`status ${response.status} for "${searchTerm}"`);
          if (!response.ok) {
            const error = new Error(`HTTP ${response.status}`);
            Object.assign(error, {
              status: response.status,
              retryAfter: Number(response.headers.get('Retry-After')) || 0,
            });
            throw error;
          }
          return response.text();
        })
        .then((markup) => {
          // The challenge page is sometimes served with a 200, so status alone
          // is not enough: anything without the results container is not a
          // suggestions response and must never reach the dropdown.
          if (!markup.includes('data-search-results')) {
            debugLog(`response for "${searchTerm}" is not suggestions (${markup.length}b)` +
              (markup.includes('challenge-error-text') ? ' — BOT CHALLENGE' : ''));
            throw new Error('Not a suggestions response');
          }
          return markup;
        })
        .finally(() => {
          clearTimeout(budget);
          abortController.signal.removeEventListener('abort', relayAbort);
        });
    };

    const fetchWithRetry = () => {
      const cached = searchResultsCache.get(searchTerm);
      if (cached) {
        debugLog(`"${searchTerm}" served from this page view's cache (no request)`);
        return Promise.resolve(cached);
      }

      return fetchAttempt().catch((error) => {
        // A dropped request fails fast, so several retries fit in the budget;
        // one that hangs burns the whole budget in a single attempt. A rate
        // limit needs the opposite of eagerness — retrying straight into a 429
        // spends the very allowance the next real search needs.
        if (abortController.signal.aborted) throw error;
        const wait =
          error?.status === 429
            ? Math.max(SEARCH_RATE_LIMIT_DELAY_MS, (error.retryAfter || 0) * 1000)
            : SEARCH_RETRY_DELAY_MS;
        if (Date.now() + wait >= deadline) {
          debugLog(`giving up on "${searchTerm}": ${error?.message ?? error}`);
          throw error;
        }
        debugLog(`retrying "${searchTerm}" in ${wait}ms after ${error?.message ?? error}`);
        return new Promise((resolve) => setTimeout(resolve, wait)).then(fetchWithRetry);
      });
    };

    fetchWithRetry()
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        if (searchResultsCache.size >= SEARCH_CACHE_MAX) {
          searchResultsCache.delete(searchResultsCache.keys().next().value);
        }
        searchResultsCache.set(searchTerm, resultsMarkup);

        morph(predictiveSearchResults, resultsMarkup);
        predictiveSearchResults.setAttribute('data-ss-query', searchTerm);

        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        debugLog(`painted "${searchTerm}" with ${resultCount} results`);
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        // Out of retries. Drop our own "Searching..." placeholder, otherwise
        // the dropdown hangs on it forever and reads as "search is broken".
        this.#clearStale(searchTerm);
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();

    // The always-visible bar never SHOWS the recently-viewed empty state —
    // header-search-bar.js prunes the container whenever the input is empty,
    // so everything below would be fetched and then immediately discarded.
    // That is two throwaway requests (the empty section, then the
    // recently-viewed products) every time the shopper clears the field.
    // /search/suggest is rate limited, and clearing the field is exactly what
    // a shopper does between two searches — so these wasted requests were
    // helping spend the allowance the NEXT real search needed. Just clear.
    if (this.classList.contains('header-search-bar')) {
      predictiveSearchResults.replaceChildren();
      predictiveSearchResults.setAttribute('data-ss-query', '');
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      // Every failure below FALLS THROUGH to the New Arrivals panel rather
      // than returning. A shopper whose recently-viewed products have since
      // been deleted or sold out gets no markup back here — and this panel
      // exists precisely so that tapping search is never a blank box, so
      // abandoning the whole reset would defeat its purpose.
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      const recentlyViewedProductsHtml = recentlyViewedMarkup
        ? new DOMParser()
            .parseFromString(recentlyViewedMarkup, 'text/html')
            .getElementById('predictive-search-products')
        : null;
      const recentlyViewedChildren = recentlyViewedProductsHtml
        ? Array.from(recentlyViewedProductsHtml.children)
        : [];
      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');

      if (collectionElement && recentlyViewedChildren.length > 0) {
        // This markup is the SEARCH RESULTS section rendered for an `id:`
        // query, so its heading says "Products" — the same words as the New
        // Arrivals heading it is replacing. Retitle it so the block reads as
        // what it is. The string arrives translated from the snippet.
        const recentlyViewedTitle = this.dataset.recentlyViewedTitle;

        for (const child of recentlyViewedChildren) {
          if (!(child instanceof HTMLElement)) continue;
          child.setAttribute('ref', 'recentlyViewedWrapper');
          if (!recentlyViewedTitle) continue;
          const heading = child.classList.contains('predictive-search-results__title')
            ? child
            : child.querySelector('.predictive-search-results__title');
          if (heading) heading.textContent = recentlyViewedTitle;
        }

        // replaceChildren, not prepend: New Arrivals is the FALLBACK for a
        // shopper with no history, not a second row underneath their own. A
        // shopper who has been browsing sees what they were looking at; a new
        // one sees what just landed. (Stacking both is a one-word change back
        // to prepend, if that is ever wanted.)
        collectionElement.replaceChildren(...recentlyViewedChildren);
      }
    }

    if (abortController.signal.aborted) return;
    // The shopper typed while this reset was fetching — their suggestions own
    // the panel now, so do not morph the empty state over the top of them.
    if (searchInput.value.trim().length > 0) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
